/**
 * turn-detail-dialog.tsx — Hộp "Chi tiết" của một câu trả lời trong Kiểm tra hỏi đáp (kiểu HelpKnow).
 *
 *   TRÁI : tin của khách + câu trả lời (bong bóng), "Xem toàn bộ cuộc hội thoại", "Sao chép ID";
 *          thẻ "Nguồn câu trả lời" (kiến thức phù hợp: loại, tiêu đề, độ liên quan %, câu hỏi chuẩn +
 *          tương tự, nút Chỉnh sửa) và "Quá trình suy luận" (dòng thời gian: suy nghĩ → gọi công cụ với
 *          Đầu vào / Đầu ra → trả lời); "Sửa câu trả lời → Lưu thành hỏi đáp".
 *   PHẢI : trợ lý "Kiểm tra / tối ưu câu trả lời" — 4 gợi ý nhanh + ô hỏi; giải thích dựa trên nhật ký
 *          thật của lượt đó và đề xuất sửa (nút làm ngay).
 */
import { useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'
import { Trans, useTranslation } from 'react-i18next'
import {
  BookOpenText, Bot, Brain, CheckCircle2, ChevronRight, Copy, Loader2, MessagesSquare, PencilLine, SendHorizonal,
  Sparkles, UserRound, Wrench, AlertTriangle, ArrowRightLeft, Save,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Textarea } from '@/components/ui/misc'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { useQueryClient } from '@tanstack/react-query'
import { KnowledgeBaseSelect } from '../knowledge/kb-base-select'
import { KNOWLEDGE_BASES_PATH } from '../knowledge/kb-lib'
import { OpenInNewTab } from '@/components/ai/open-in-new-tab'
import { apiError } from '@/lib/api-client'
import { scrollContainerToEnd } from '@/lib/scroll-to-end'
import { cn } from '@/lib/utils'
import {
  explainTurn, fetchTurnDetail, saveTurnFeedback, EXPLAIN_QUICK_LABELS,
  type ExplainQuick, type ExplainSuggestion, type TurnDetail, type DetailStep, type AutoStopNotice,
} from '@/hooks/use-ai-train-test'
import { EditKnowledgeByIdDialog } from './qa-form'
import { MATCH_LABELS, SOURCE_TYPE_LABELS, scoreTone, jsonPreview, parseQuestionLines } from './train-lib'

export interface TestTurn {
  key: string
  customerText: string
  reply: string | null
  bubbles: string[]
  runId?: string
  handoffReason?: string | null
  /** Câu báo khách khi chuyển người (khách thật nhận khi AI tự trả lời). */
  handoffNotice?: string | null
  /** Câu chờ ("Trả lời kỹ lưỡng") gửi trước câu trả lời. */
  waitNotice?: string | null
  /** Ghi chú độ trễ trên kênh thật (chat thử không chờ). */
  delayNote?: string | null
  detail?: TurnDetail
  /** Tự động ngừng trả lời (lượt này AI không chạy). */
  autoStop?: AutoStopNotice | null
}

type ChatLine = { role: 'user' | 'assistant'; content: string; suggestions?: ExplainSuggestion[] }

const STEP_ICON: Record<DetailStep['kind'], typeof Brain> = {
  thought: Brain, tool: Wrench, check: CheckCircle2, answer: Bot, handoff: ArrowRightLeft, error: AlertTriangle,
}

function StepTimeline({ steps }: { steps: DetailStep[] }) {
  const { t } = useTranslation('aiTrainUi')
  if (steps.length === 0) {
    return <p className="py-6 text-center text-xs text-muted-foreground">{t('turnDetail.noLog')}</p>
  }
  return (
    <ol className="relative space-y-3 border-l pl-4">
      {steps.map((s, i) => {
        const Icon = STEP_ICON[s.kind] ?? Brain
        return (
          <li key={i} className="relative">
            <span className={cn(
              'absolute -left-[23px] top-0.5 flex h-4 w-4 items-center justify-center rounded-full border bg-background',
              s.kind === 'error' && 'border-destructive text-destructive',
            )}>
              <Icon className="h-2.5 w-2.5" />
            </span>
            <div className="flex flex-wrap items-baseline gap-x-2">
              <span className="text-sm font-medium">{s.title}</span>
              {s.latencyMs != null && <span className="text-[11px] text-muted-foreground">{t('turnDetail.seconds', { value: (s.latencyMs / 1000).toFixed(1) })}</span>}
            </div>
            {s.summary && <p className="whitespace-pre-wrap text-xs text-muted-foreground">{s.summary}</p>}
            {s.kind === 'tool' && s.input !== undefined && (
              <details className="mt-1 rounded border bg-muted/30 px-2 py-1 text-xs">
                <summary className="cursor-pointer select-none font-medium">{t('turnDetail.input')}</summary>
                <pre className="mt-1 max-h-48 overflow-auto whitespace-pre-wrap break-words font-mono text-[11px]">{jsonPreview(s.input)}</pre>
              </details>
            )}
            {s.kind === 'tool' && s.output !== undefined && (
              <details className="mt-1 rounded border bg-muted/30 px-2 py-1 text-xs">
                <summary className="cursor-pointer select-none font-medium">{t('turnDetail.output')}</summary>
                <pre className="mt-1 max-h-64 overflow-auto whitespace-pre-wrap break-words font-mono text-[11px]">{jsonPreview(s.output)}</pre>
              </details>
            )}
          </li>
        )
      })}
    </ol>
  )
}

export function TurnDetailDialog({ turn, botId, conversation, onClose, onGuide }: {
  turn: TestTurn
  botId: string
  /** Toàn bộ phiên thử (để "Xem toàn bộ cuộc hội thoại"). */
  conversation: TestTurn[]
  onClose: () => void
  /** Chuyển sang thẻ "Hướng dẫn AI" với nội dung gợi ý (sửa tính cách / quy tắc). */
  onGuide?: (text: string) => void
}) {
  const { t } = useTranslation('aiTrainUi')
  const [detail, setDetail] = useState<TurnDetail | null>(turn.detail ?? null)
  const [loading, setLoading] = useState(!turn.detail && !!turn.runId)
  const [showAll, setShowAll] = useState(false)
  const [editId, setEditId] = useState<string | null>(null)
  // Sửa câu trả lời → lưu hỏi đáp
  const [fixOpen, setFixOpen] = useState(false)
  const [fixQuestion, setFixQuestion] = useState(turn.customerText)
  const [fixSimilar, setFixSimilar] = useState('')
  const [fixAnswer, setFixAnswer] = useState(turn.reply ?? '')
  // '' = tự chọn: kho đầu tiên nhân viên AI đang tra, không giới hạn thì "Kho mặc định" (máy chủ quyết định).
  const [fixBase, setFixBase] = useState('')
  const qc = useQueryClient()
  const [saving, setSaving] = useState(false)
  // Trợ lý giải thích
  const [chat, setChat] = useState<ChatLine[]>([])
  const [ask, setAsk] = useState('')
  const [thinking, setThinking] = useState(false)
  const chatEnd = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (turn.detail || !turn.runId) return
    let alive = true
    fetchTurnDetail(turn.runId)
      .then((r) => { if (alive) setDetail(r.detail) })
      .catch((err) => toast.error(apiError(err)))
      .finally(() => { if (alive) setLoading(false) })
    return () => { alive = false }
  }, [turn.runId, turn.detail])

  useEffect(() => { scrollContainerToEnd(chatEnd.current) }, [chat, thinking])

  const sources = (detail?.sources ?? []).filter((s) => s.type === 'faq' || s.type === 'article')
  const otherSources = (detail?.sources ?? []).filter((s) => s.type !== 'faq' && s.type !== 'article')
  const bubbles = turn.bubbles.length ? turn.bubbles : turn.reply ? [turn.reply] : []

  async function runExplain(opts: { quick?: ExplainQuick; question?: string }) {
    if (!turn.runId) { toast.error(t('turnDetail.noRunId')); return }
    const label = opts.quick ? EXPLAIN_QUICK_LABELS[opts.quick] : (opts.question ?? '')
    if (!label.trim()) return
    const history = chat.map((c) => ({ role: c.role, content: c.content }))
    setChat((c) => [...c, { role: 'user', content: label }])
    setThinking(true)
    try {
      const r = await explainTurn({
        runId: turn.runId, botId, customerText: turn.customerText, reply: turn.reply, handoffReason: turn.handoffReason ?? null,
        ...(opts.quick ? { quick: opts.quick } : { question: label }), history,
      })
      setChat((c) => [...c, { role: 'assistant', content: r.answer, suggestions: r.suggestions }])
    } catch (err) {
      setChat((c) => [...c, { role: 'assistant', content: t('turnDetail.error', { message: apiError(err) }) }])
    } finally {
      setThinking(false)
    }
  }

  function applySuggestion(s: ExplainSuggestion) {
    if (s.kind === 'edit_knowledge' && s.entryId) { setEditId(s.entryId); return }
    if (s.kind === 'add_faq') {
      setFixQuestion(s.question || turn.customerText)
      setFixAnswer(s.answer || turn.reply || '')
      setFixOpen(true)
      return
    }
    if (s.kind === 'edit_persona') { onGuide?.(`${s.title}: ${s.detail}`); return }
  }

  async function saveFix(saveAs: 'faq') {
    if (!fixQuestion.trim() || !fixAnswer.trim()) { toast.error(t('turnDetail.needBoth')); return }
    setSaving(true)
    try {
      const r = await saveTurnFeedback({
        runId: turn.runId, customerText: fixQuestion.trim(), correctAnswer: fixAnswer.trim(), saveAs,
        similarQuestions: parseQuestionLines(fixSimilar, fixQuestion), originalReply: turn.reply,
        knowledgeBaseId: fixBase || null, botId,
      })
      void qc.invalidateQueries({ queryKey: ['knowledge-categories'] })
      void qc.invalidateQueries({ queryKey: ['knowledge'] })
      const catName = r.knowledgeBaseName ?? ''
      toast.success(catName ? t('turnDetail.savedToBase', { name: catName }) : t('turnDetail.saved'))
      setFixOpen(false)
    } catch (err) {
      toast.error(apiError(err))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[94vh] overflow-hidden p-0 sm:max-w-6xl">
        <DialogHeader className="border-b px-5 py-3">
          <DialogTitle>{t('turnDetail.title')}</DialogTitle>
          <DialogDescription>{t('turnDetail.description')}</DialogDescription>
        </DialogHeader>
        <div className="grid max-h-[calc(94vh-72px)] min-h-0 grid-cols-1 overflow-hidden lg:grid-cols-[minmax(0,1.25fr)_minmax(0,1fr)]">
          {/* ══ TRÁI ══ */}
          <div className="min-h-0 space-y-3 overflow-y-auto border-r p-4">
            <div className="space-y-2 rounded-lg border bg-muted/30 p-3">
              <div className="flex gap-2">
                <UserRound className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                <p className="whitespace-pre-wrap text-sm">{turn.customerText}</p>
              </div>
              <div className="flex gap-2">
                <Bot className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                <div className="min-w-0 flex-1 space-y-1">
                  {bubbles.length ? bubbles.map((b, i) => (
                    <p key={i} className="whitespace-pre-wrap rounded-md border bg-background px-2.5 py-1.5 text-sm">{b}</p>
                  )) : (
                    <p className="text-sm italic text-muted-foreground">
                      {turn.handoffReason ? t('turnDetail.handoffTo', { reason: turn.handoffReason }) : t('turnDetail.noReply')}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex flex-wrap gap-2 pt-1">
                <Button variant="outline" size="sm" onClick={() => setShowAll((v) => !v)}>
                  <MessagesSquare className="h-3.5 w-3.5" /> {showAll ? t('turnDetail.hideConversation') : t('turnDetail.showConversation')}
                </Button>
                {turn.runId && (
                  <Button
                    variant="ghost" size="sm"
                    onClick={() => { void navigator.clipboard?.writeText(turn.runId!); toast.success(t('turnDetail.idCopied')) }}
                  >
                    <Copy className="h-3.5 w-3.5" /> {t('turnDetail.copyId')}
                  </Button>
                )}
                <Button variant="ghost" size="sm" onClick={() => setFixOpen((v) => !v)}>
                  <PencilLine className="h-3.5 w-3.5" /> {t('turnDetail.fixAnswer')}
                </Button>
              </div>
              {showAll && (
                <div className="max-h-56 space-y-1 overflow-y-auto rounded border bg-background p-2 text-xs">
                  {conversation.map((tn) => (
                    <div key={tn.key} className={cn('space-y-0.5', tn.key === turn.key && 'rounded bg-primary/5 p-1')}>
                      <p><b>{t('turnDetail.customerLabel')}</b> {tn.customerText}</p>
                      <p className="whitespace-pre-wrap"><b>AI:</b> {tn.reply ?? (tn.handoffReason ? t('turnDetail.convHandoff', { reason: tn.handoffReason }) : t('turnDetail.convNoReply'))}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {fixOpen && (
              <div className="space-y-2 rounded-lg border border-primary/30 bg-primary/5 p-3">
                <p className="text-sm font-medium">{t('turnDetail.fixTitle')}</p>
                <Input value={fixQuestion} onChange={(e) => setFixQuestion(e.target.value)} placeholder={t('turnDetail.fixQuestionPlaceholder')} />
                <Textarea className="min-h-16 text-sm" value={fixSimilar} onChange={(e) => setFixSimilar(e.target.value)} placeholder={t('turnDetail.fixSimilarPlaceholder')} />
                <Textarea className="min-h-28 text-sm" value={fixAnswer} onChange={(e) => setFixAnswer(e.target.value)} placeholder={t('turnDetail.fixAnswerPlaceholder')} />
                <div className="flex flex-wrap items-center gap-2 text-xs">
                  <span className="text-muted-foreground">{t('turnDetail.saveToBase')}</span>
                  <KnowledgeBaseSelect
                    className="h-8 w-72 text-xs" value={fixBase} onChange={setFixBase} ariaLabel={t('turnDetail.baseAria')}
                    autoLabel={t('turnDetail.autoBase')}
                  />
                </div>
                <div className="flex flex-wrap justify-end gap-2">
                  <Button variant="ghost" size="sm" onClick={() => setFixOpen(false)}>{t('common:actions.cancel')}</Button>
                  <Button size="sm" disabled={saving} onClick={() => void saveFix('faq')}>
                    {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} {t('turnDetail.saveAsQa')}
                  </Button>
                </div>
              </div>
            )}

            {loading ? (
              <p className="flex items-center gap-2 py-6 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> {t('turnDetail.loadingDetail')}</p>
            ) : (
              <Tabs defaultValue="sources">
                <TabsList>
                  <TabsTrigger value="sources">{t('turnDetail.tabSources')}</TabsTrigger>
                  <TabsTrigger value="steps">{t('turnDetail.tabSteps')}</TabsTrigger>
                </TabsList>
                <TabsContent value="sources" className="space-y-2">
                  <p className="text-xs font-medium text-muted-foreground">
                    {t('turnDetail.matchedKnowledge', { count: sources.length })}
                    {detail?.threshold != null && (
                      <span className="font-normal">{t('turnDetail.thresholdNote', { pct: Math.round(detail.threshold * 100) })}</span>
                    )}
                  </p>
                  {sources.length === 0 && (
                    <p className="rounded border border-dashed p-4 text-center text-xs text-muted-foreground">
                      {t('turnDetail.noKnowledge')}
                    </p>
                  )}
                  {sources.map((s) => (
                    <div key={`${s.tool}:${s.id ?? s.title}`} className="space-y-1 rounded-lg border p-2.5">
                      <div className="flex flex-wrap items-center gap-1.5">
                        <Badge variant="outline">{SOURCE_TYPE_LABELS[s.type] ?? s.type}{s.knowledgeBaseName ? ` · ${s.knowledgeBaseName}` : ''}</Badge>
                        <span className="min-w-0 flex-1 truncate text-sm font-medium" title={s.title}>{s.canonicalQuestion ?? s.title}</span>
                        {s.scorePct != null && <Badge variant={scoreTone(s.scorePct)}>{t('turnDetail.relevance', { pct: s.scorePct })}</Badge>}
                        {s.id && (
                          <Button variant="ghost" size="sm" className="h-7 px-2" onClick={() => setEditId(s.id!)}>
                            <PencilLine className="h-3.5 w-3.5" /> {t('turnDetail.edit')}
                          </Button>
                        )}
                      </div>
                      <p className="text-[11px] text-muted-foreground">
                        {s.match ? MATCH_LABELS[s.match] ?? s.match : ''}
                        {s.categoryName ? ` · ${s.categoryName}` : ''}
                        {s.status === 'archived' ? t('turnDetail.disabledSuffix') : ''}
                      </p>
                      {s.matchedQuestion && s.matchedQuestion !== (s.canonicalQuestion ?? s.title) && (
                        <p className="text-xs"><Trans t={t} i18nKey="turnDetail.matchedQuestion" values={{ question: s.matchedQuestion }} components={{ i: <i /> }} /></p>
                      )}
                      {!!s.similarQuestions?.length && (
                        <p className="text-xs text-muted-foreground">{t('turnDetail.similarQuestions', { list: s.similarQuestions.slice(0, 6).join(' · ') })}</p>
                      )}
                      {s.snippet && <p className="line-clamp-3 whitespace-pre-wrap text-xs text-muted-foreground">{s.snippet}</p>}
                    </div>
                  ))}
                  {otherSources.length > 0 && (
                    <>
                      <p className="pt-1 text-xs font-medium text-muted-foreground">{t('turnDetail.otherSources', { count: otherSources.length })}</p>
                      {otherSources.map((s) => (
                        <div key={`${s.tool}:${s.id ?? s.title}`} className="flex items-center gap-2 rounded border px-2.5 py-1.5 text-sm">
                          <Badge variant="outline">{SOURCE_TYPE_LABELS[s.type] ?? s.type}</Badge>
                          <span className="min-w-0 flex-1 truncate">{s.title}</span>
                          {s.scorePct != null && <Badge variant={scoreTone(s.scorePct)}>{s.scorePct}%</Badge>}
                        </div>
                      ))}
                    </>
                  )}
                </TabsContent>
                <TabsContent value="steps">
                  <StepTimeline steps={detail?.steps ?? []} />
                </TabsContent>
              </Tabs>
            )}
          </div>

          {/* ══ PHẢI: Kiểm tra / tối ưu câu trả lời ══ */}
          <div className="flex min-h-0 flex-col p-4">
            <p className="mb-2 flex items-center gap-1.5 text-sm font-medium"><Sparkles className="h-4 w-4 text-primary" /> {t('turnDetail.assistantTitle')}</p>
            <div className="min-h-0 flex-1 space-y-2 overflow-y-auto rounded-lg border bg-muted/20 p-3">
              {chat.length === 0 && (
                <div className="space-y-2">
                  <p className="text-xs text-muted-foreground">{t('turnDetail.assistantIntro')}</p>
                  {(Object.keys(EXPLAIN_QUICK_LABELS) as ExplainQuick[]).map((k) => (
                    <button
                      key={k} type="button" disabled={thinking}
                      className="flex w-full items-center justify-between rounded-md border bg-background px-3 py-2 text-left text-sm hover:border-primary/60"
                      onClick={() => void runExplain({ quick: k })}
                    >
                      {EXPLAIN_QUICK_LABELS[k]} <ChevronRight className="h-4 w-4 text-muted-foreground" />
                    </button>
                  ))}
                </div>
              )}
              {chat.map((c, i) => (
                <div key={i} className={cn('flex', c.role === 'user' ? 'justify-end' : 'justify-start')}>
                  <div className={cn(
                    'max-w-[92%] space-y-2 whitespace-pre-wrap rounded-lg px-3 py-2 text-sm',
                    c.role === 'user' ? 'bg-primary text-primary-foreground' : 'border bg-background',
                  )}>
                    <p>{c.content}</p>
                    {!!c.suggestions?.length && (
                      <div className="space-y-1.5">
                        {c.suggestions.map((s, j) => (
                          <div key={j} className="rounded border bg-muted/40 p-2 text-xs">
                            <p className="font-medium">{s.title}</p>
                            {s.detail && <p className="text-muted-foreground">{s.detail}</p>}
                            {s.kind === 'add_faq' && s.question && <p className="mt-1"><b>{t('turnDetail.ask')}</b> {s.question}<br /><b>{t('turnDetail.answer')}</b> {s.answer}</p>}
                            {s.kind !== 'other' && (
                              <Button size="sm" variant="outline" className="mt-1.5 h-7" onClick={() => applySuggestion(s)}>
                                {s.kind === 'edit_knowledge' ? t('turnDetail.editEntry') : s.kind === 'add_faq' ? t('turnDetail.addQa') : t('turnDetail.openGuide')}
                              </Button>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
              {thinking && <p className="flex items-center gap-2 text-xs italic text-muted-foreground"><Loader2 className="h-3.5 w-3.5 animate-spin" /> {t('turnDetail.readingLog')}</p>}
              <div ref={chatEnd} />
            </div>
            {chat.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1">
                {(Object.keys(EXPLAIN_QUICK_LABELS) as ExplainQuick[]).map((k) => (
                  <button key={k} type="button" disabled={thinking} onClick={() => void runExplain({ quick: k })}
                    className="rounded-full border px-2 py-0.5 text-[11px] hover:border-primary/60">
                    {EXPLAIN_QUICK_LABELS[k]}
                  </button>
                ))}
              </div>
            )}
            <div className="mt-2 flex gap-2">
              <Input
                placeholder={t('turnDetail.askMore')} value={ask} disabled={thinking}
                onChange={(e) => setAsk(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey && ask.trim()) { e.preventDefault(); const q = ask; setAsk(''); void runExplain({ question: q }) } }}
              />
              <Button disabled={thinking || !ask.trim()} onClick={() => { const q = ask; setAsk(''); void runExplain({ question: q }) }}>
                <SendHorizonal className="h-4 w-4" />
              </Button>
            </div>
            <OpenInNewTab to={KNOWLEDGE_BASES_PATH} variant="link" size="sm" className="mt-1 self-start px-0" title={t('turnDetail.openKbTitle')}>
              <BookOpenText className="h-3.5 w-3.5" /> {t('turnDetail.openKb')}
            </OpenInNewTab>
          </div>
        </div>
        {editId && <EditKnowledgeByIdDialog id={editId} onClose={() => setEditId(null)} />}
      </DialogContent>
    </Dialog>
  )
}
